import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { SETTING_COLORS, SETTING_COLORS_DARK, VIEW_TYPES, LANGUAGES } from '../constants';

const ControlPanel = ({
  fileInfo,
  selectedMethod,
  setSelectedMethod,
  selectedModel,
  setSelectedModel,
  viewType,
  setViewType,
  selectedLanguages,
  setSelectedLanguages,
  selectedSettings,
  setSelectedSettings,
  selectedMethodsForComparison,
  setSelectedMethodsForComparison,
  comparisonModel,
  setComparisonModel,
  heatmapMetric,
  setHeatmapMetric,
  statisticalView,
  setStatisticalView,
  methodData,
  experimentsMetadata,
  availableSettings
}) => {
  const { isDarkMode } = useTheme();
  const settingColors = isDarkMode ? SETTING_COLORS_DARK : SETTING_COLORS;

  // Filter methods based on selected settings
  const filteredMethods = useMemo(() => {
    const allMethods = Object.keys(methodData);
    if (!selectedSettings || selectedSettings.length === 0 || !experimentsMetadata) {
      return allMethods;
    }
    return allMethods.filter(method => {
      const metadata = experimentsMetadata[method];
      // Include methods whose setting matches selectedSettings, or methods with unknown setting
      return metadata && (selectedSettings.includes(metadata.setting) || metadata.setting === 'unknown');
    });
  }, [methodData, experimentsMetadata, selectedSettings]);

  const models = selectedMethod ? Object.keys(methodData[selectedMethod] || {}) : [];

  // Get setting badge for a method
  const getMethodSetting = (method) => {
    if (!experimentsMetadata || !experimentsMetadata[method]) return null;
    return experimentsMetadata[method].setting;
  };

  return (
    <section className="mb-6">
      <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200">
        {/* Settings Filter Section */}
        {availableSettings && availableSettings.length > 0 && (
          <div className="mb-6 pb-6 border-b" style={{ borderColor: 'var(--border-primary)' }}>
            <h3 className="text-base font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
              Experimental Settings
            </h3>
            <div className="flex flex-wrap gap-3 justify-center">
              {['monolingual', 'multilingual', 'cross-lingual'].map(setting => {
                const isAvailable = availableSettings.includes(setting);
                const isSelected = selectedSettings?.includes(setting);
                return (
                  <button
                    key={setting}
                    onClick={() => {
                      if (!isAvailable) return;
                      setSelectedSettings(prev => {
                        if (prev.includes(setting)) {
                          const newSettings = prev.filter(s => s !== setting);
                          return newSettings.length > 0 ? newSettings : prev;
                        }
                        return [...prev, setting];
                      });
                    }}
                    disabled={!isAvailable}
                    className={`setting-btn ${setting} ${isSelected ? 'active' : ''}`}
                    style={{
                      opacity: isAvailable ? 1 : 0.4,
                      cursor: isAvailable ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {setting}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Experiment/Method Selection */}
          <div className="space-y-2">
            <label htmlFor="method-select" className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Experiment
            </label>
            <select
              id="method-select"
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-primary)'
              }}
            >
              {filteredMethods.map(method => (
                <option key={method} value={method}>
                  {method}
                </option>
              ))}
            </select>
            {selectedMethod && experimentsMetadata?.[selectedMethod] && (
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: settingColors[experimentsMetadata[selectedMethod].setting] + '20',
                    color: settingColors[experimentsMetadata[selectedMethod].setting]
                  }}
                >
                  {experimentsMetadata[selectedMethod].setting}
                </span>
              </div>
            )}
          </div>

          {/* Model Selection */}
          <div className="space-y-2">
            <label htmlFor="model-select" className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Model
            </label>
            <select
              id="model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-primary)'
              }}
            >
              {models.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          {/* View Type Selection */}
          <div className="space-y-2">
            <label htmlFor="view-select" className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              Visualization
            </label>
            <select
              id="view-select"
              value={viewType}
              onChange={(e) => setViewType(e.target.value)}
              className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border-primary)',
                color: 'var(--text-primary)'
              }}
            >
              {VIEW_TYPES.map(vt => (
                <option key={vt.value} value={vt.value}>
                  {vt.icon} {vt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Statistical View Selection */}
          {viewType === 'statistical' && (
            <div className="space-y-2">
              <label className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Statistic
              </label>
              <div className="flex flex-wrap gap-2">
                {['mean', 'std', 'max', 'min', 'trend'].map(stat => (
                  <button
                    key={stat}
                    onClick={() => setStatisticalView(stat)}
                    className={`toggle-btn ${statisticalView === stat ? 'active' : ''}`}
                  >
                    {stat.charAt(0).toUpperCase() + stat.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Language Selection */}
        <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-primary)' }}>
          <h3 className="text-sm font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
            Languages
          </h3>
          <div className="flex flex-wrap gap-2 justify-center">
            {LANGUAGES.map(lang => (
              <button
                key={lang}
                onClick={() => {
                  setSelectedLanguages(prev => {
                    if (prev.includes(lang)) {
                      const newLangs = prev.filter(l => l !== lang);
                      return newLangs.length > 0 ? newLangs : prev;
                    }
                    return [...prev, lang].sort();
                  });
                }}
                className={`language-btn ${lang} ${selectedLanguages.includes(lang) ? 'active' : ''}`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>

        {/* Method Comparison Controls */}
        {(viewType === 'methodComparison' || viewType === 'settingComparison') && (
          <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-primary)' }}>
            <h3 className="text-base font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
              Comparison Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Select Experiments to Compare
                </label>
                <div className="max-h-48 overflow-y-auto space-y-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  {filteredMethods.map(method => {
                    const setting = getMethodSetting(method);
                    return (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedMethodsForComparison.includes(method)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMethodsForComparison(prev => [...prev, method]);
                            } else {
                              setSelectedMethodsForComparison(prev => prev.filter(m => m !== method));
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{method}</span>
                        {setting && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded font-medium"
                            style={{
                              backgroundColor: settingColors[setting] + '20',
                              color: settingColors[setting]
                            }}
                          >
                            {setting}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="comparison-model-select" className="block text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Model for Comparison
                </label>
                <select
                  id="comparison-model-select"
                  value={comparisonModel}
                  onChange={(e) => setComparisonModel(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border-primary)',
                    color: 'var(--text-primary)'
                  }}
                >
                  {fileInfo?.models.map(model => (
                    <option key={model} value={model}>{model}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Heatmap Controls */}
        {viewType === 'heatmap' && (
          <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: 'var(--border-primary)' }}>
            <label className="block text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              Heatmap Metric
            </label>
            <div className="flex flex-wrap gap-3 justify-center">
              {['absolute', 'improvement', 'relative'].map(metric => (
                <button
                  key={metric}
                  onClick={() => setHeatmapMetric(metric)}
                  className={`toggle-btn ${heatmapMetric === metric ? 'active' : ''}`}
                >
                  {metric.charAt(0).toUpperCase() + metric.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ControlPanel;
